import { Injectable } from '@angular/core'
import { CreateEventInput, EventType, EventWithStatus, EVENT_ID, Event, ResponseEvent, UpdateEventInput, LogGaterix } from '@app/models/event.model'
import {
  GET_CATEGORIES,
  GET_EVENTS,
  SEND_MESSAGE,
  SET_EVENT,
  OPEN_DOORS,
  DELETE_EVENT,
  UPDATE_EVENT,
  RESET_SCANS,
  GET_LOGS,
  GET_FILTERED_EVENTS_WITH,
  GET_EVENT,
} from '@app/query/event.query'
import { catchError, map, Observable, of, shareReplay, Subject, tap } from 'rxjs'

import { ServicesSubscriberEvent } from '@app/models/others.model'
import { TranslateService } from '@ngx-translate/core'
import { ConfirmationService, MessageService } from 'primeng/api'
import { serializeAs } from '@app/utils/serialize'
import { pick } from 'lodash-es'
import { EventUpdateLocation } from '@app/models/location.model'
import { base64ToArrayBuffer, downloadBytes } from '@app/utils/download'
import { TranslateMessagesService } from '@app/shared/services/translate-messages.service'
import { ApiService } from '@app/core/graphql/services/api.service'
import { DateService } from '@app/shared/services/date.service'
import { PassUploadJobsService } from '@app/shared/services/pass-upload-jobs.service'

@Injectable({
  providedIn: 'root',
})
export class EventsService {
  subscriber: Subject<ServicesSubscriberEvent> = new Subject<ServicesSubscriberEvent>()
  id: string = 'EVENT'
  sysMessage: any

  /** Cache of the RAW get_categories response, keyed by module (or '__all__'). event_types are a
   *  fixed per-deployment taxonomy (residential/employees/…), not client-editable, so the list is
   *  fetched once per session instead of on every page. Only the raw response is cached; translation
   *  is applied downstream so a language switch still re-translates the names. */
  private categoriesCache = new Map<string, Observable<any>>()
  constructor(
    private API: ApiService,
    private confirmationService: ConfirmationService,
    private translateMessagesService: TranslateMessagesService,
    private messageService: MessageService,
    private translate: TranslateService,
    private dateService: DateService,
    private passUploadJobs: PassUploadJobsService,
  ) {
    this.sysMessage = this.translateMessagesService.create(this.id)
  }

  /**
   * YB-1669: a tickets_url excel was staged + auto-queued — track it in the bell panel
   * (errors dialog opens if rows failed validation).
   * @returns whether the response carried a staged job, i.e. whether the bell/dialog owns it.
   */
  private trackStagedUpload(data: any): boolean {
    const jobId = data?.data?.pass_upload_job_id
    if (!data?.success || !jobId) return false
    this.passUploadJobs.trackJob(+jobId)
    return true
  }

  createNew(payload: Partial<CreateEventInput>, silence?: boolean) {
    return this.API.privateMutation<any>(SET_EVENT, (data: any) => {
      return {
        data: serializeAs(CreateEventInput, pick(Object.assign(payload, data), Object.keys(new CreateEventInput()))) as CreateEventInput,
      }
    }).pipe(
      tap((data: any) => {
        this.trackStagedUpload(data)
        if (silence) return data
        if (data.success) {
          this.messageService.add({
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
            ...this.sysMessage.message('CREATE_SUCCESS', data.data as ResponseEvent),
          })
          this.subscriber.next({
            type: `SET_${this.id}`,
            data: data.data as ResponseEvent,
          })
        }
      }),
    )
  }

  openDoors(event_id: any, keep_open: boolean, silence?: boolean) {
    return this.API.privateMutation<any>(OPEN_DOORS, (_data: any) => {
      return {
        event_id: parseInt(event_id),
        keep_open,
      }
    }).pipe(
      tap((data: any) => {
        if (silence) return data
        if (data.success) {
          this.messageService.add({
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
            ...this.sysMessage.message(keep_open ? 'OPEN_DOORS_SUCCESS_OPENED' : 'OPEN_DOORS_SUCCESS_CLOSED', data.data as ResponseEvent),
          })
          this.subscriber.next({
            type: `OPEN_DOORS`,
            data: data.data as ResponseEvent,
          })
        }
      }),
    )
  }

  getCategories(allCategogries?: boolean) {
    const module = allCategogries ? null : localStorage.getItem('CURRENT_MODULE') || 'SportsAndCulture'
    const cacheKey = allCategogries ? '__all__' : module!

    let raw$ = this.categoriesCache.get(cacheKey)
    if (!raw$) {
      raw$ = this.API.privateQuery<any>(GET_CATEGORIES, () => ({ module })).pipe(
        // Drop a failed fetch from the cache so the next call retries instead of replaying the error.
        catchError((err) => {
          this.categoriesCache.delete(cacheKey)
          throw err
        }),
        shareReplay(1),
      )
      this.categoriesCache.set(cacheKey, raw$)
    }

    // Translate on every subscribe (not inside the cache) so switching language re-translates, and
    // map to NEW objects so the cached raw response is never mutated in place.
    return raw$.pipe(
      map((response: any) => {
        const result: EventType[] = []
        for (const category of response) {
          result.push({ ...category, slug: category.name, name: this.translate.instant('CATEGORIES.' + String(category.name).toUpperCase()) })
        }
        return result
      }),
      tap((categories: EventType[] | []) => {
        this.subscriber.next({
          type: `GET_CATEGORIES`,
          data: categories,
        })
      }),
    )
  }

  safeDeleteEvent(event: Partial<Event>, silence?: boolean) {
    return new Observable<any>((observer) => {
      this.confirmationService.confirm({
        ...this.sysMessage.confirm('CONFIRM_DELETE', event as Event),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translate.instant('COMMON.BUTTONS.DELETE'),
        rejectLabel: this.translate.instant('COMMON.BUTTONS.CANCEL'),
        defaultFocus: 'reject',
        acceptButtonStyleClass: 'p-button-text',
        accept: () => {
          this.deleteEvent(event.id!, silence).subscribe((data: any) => {
            observer.next(data)
            observer.complete()
          })
        },
        reject: () => {
          observer.next({
            success: false,
          })
          observer.complete()
        },
      })
    })
  }

  deleteEvent(eventId: EVENT_ID, silence?: boolean) {
    return this.API.privateMutation<any>(DELETE_EVENT, (data: any) => {
      data.id = parseInt(eventId as string)
      data.active = false
      return {
        data,
      }
    }).pipe(
      tap((data: any) => {
        if (silence) return data
        if (data.success) {
          this.messageService.add({
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
            ...this.sysMessage.message('DELETE_SUCCESS', data.data as Partial<Event>),
          })
          this.subscriber.next({
            type: `DELETE_${this.id}`,
            data: data.data,
          })
        }
      }),
    )
  }

  resetScans(event_id: EVENT_ID, silence?: boolean) {
    return this.API.privateMutation<any>(RESET_SCANS, (data: any) => {
      data.event_id = parseInt(event_id as string)
      return data
    }).pipe(
      tap((data: any) => {
        if (silence) return data
        if (data.success) {
          this.messageService.add({
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
            ...this.sysMessage.message('RESET_SCANS_SUCCESS'),
          })
        }
      }),
    )
  }

  sendMessage(eventIds: any[], payload: any, silence?: boolean) {
    return this.API.privateQuery<any>(SEND_MESSAGE, (data: any) => {
      data = pick(Object.assign(payload, data), 'subject', 'message', 'message_type')
      data.event_ids = eventIds.map((el) => parseInt(el))
      return {
        data,
      }
    }).pipe(
      tap((data: any) => {
        if (silence) return data
        if (data.success) {
          this.messageService.add({
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
            ...this.sysMessage.message('SEND_MESSAGE_SUCCESS'),
          })
          this.subscriber.next({
            type: `SEND_MESSAGE_${this.id}`,
            data: data.data as Pick<Event, 'id'>,
          })
        }
      }),
    )
  }

  update(payload: any, silence?: boolean) {
    if (payload.update_location) {
      if ('object' == typeof payload.update_location.name) {
        payload.update_location.name = payload.update_location.name?.name || null
      }
      if (!payload.update_location.lat || !payload.update_location.lon) {
        payload.update_location = null
      } else {
        payload.update_location = serializeAs(EventUpdateLocation, pick(payload.update_location, Object.keys(new EventUpdateLocation())))
      }
    }
    return this.API.privateMutation<any>(UPDATE_EVENT, (data: any) => {
      const allowedKeys = Object.keys(new UpdateEventInput())
      if (!allowedKeys.includes('secured')) {
        allowedKeys.push('secured')
      }

      const obj = pick(Object.assign(payload, data), allowedKeys)

      return {
        data: serializeAs(UpdateEventInput, obj) as UpdateEventInput,
      }
    }).pipe(
      tap((data: any) => {
        // Note: errorText is already handled by ApiService, no need to show it again here
        // YB-1669: a staged upload returns the error file AND a job id — the errors dialog
        // owns it from there (it has a download button), so auto-downloading as well handed
        // the user the same workbook twice. Only the non-staged paths (all-or-nothing
        // rejection, queue disabled) still get the file pushed at them, since nothing opens.
        const staged = this.trackStagedUpload(data)
        if (data.errorFile && !staged) {
          downloadBytes(base64ToArrayBuffer(data.errorFile), 'errors.xlsx')
        }
        if (silence) return data
        if (data.success) {
          this.messageService.add({
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
            ...this.sysMessage.message('UPDATE_SUCCESS', data.data as Event),
          })
          this.subscriber.next({
            type: `UPDATE_${this.id}`,
            data: data.data as Event,
          })
        }
      }),
    )
  }

  getEventLogs(event_id: string) {
    return this.API.privateQuery<any>(GET_LOGS, (_data: any) => {
      return {
        event_id: parseInt(event_id),
      }
    }).pipe(
      map((response: any) => {
        if (response.success) {
          return response.data as LogGaterix[]
        } else {
          return [] as LogGaterix[]
        }
      }),
    )
  }

  getEvents() {
    return this.API.privateQuery<any>(GET_EVENTS).pipe(
      map((response: any) => {
        if (response.success) {
          return response.data as EventWithStatus[]
        } else {
          return [] as EventWithStatus[]
        }
      }),
      tap((events: EventWithStatus[] | []) => {
        this.subscriber.next({
          type: `GET_${this.id}S`,
          data: events,
        })
      }),
    )
  }

  getEvent(event_id: string) {
    return this.API.privateQuery<any>(GET_EVENT, (data: any) => {
      data.id = parseInt(event_id)
      return data
    }).pipe(
      map((response: any) => {
        if (response.success) {
          return response.data as EventWithStatus
        } else {
          return {} as EventWithStatus
        }
      }),
    )
  }

  getEventsBy(payload: any, requestFields: string | null = null, futureOnly: boolean = false, pageOptions: any = {}): Observable<EventWithStatus[]> {
    return this.API.privateQuery<any>(GET_FILTERED_EVENTS_WITH(requestFields), (data: any) => {
      const r = Object.assign(data, payload)
      if (r.client_id != null) r.client_id = Number(r.client_id)
      if (futureOnly) {
        r.status = 'future_and_incomplete'
      }
      const d: any = { data: r }
      if (pageOptions) d.pageOptions = pageOptions
      return d
    }).pipe(
      map((response: any): EventWithStatus[] => {
        if (response.success) {
          return response.data.map((e: any) => {
            e.passesLength = e.event_statistics?.totalTickets || 0
            e.entrancesLength = e?.entrances_statistics?.filter((en: any) => en.name !== 'event_unused_entrance').length || 0
            return e as EventWithStatus
          })
        } else {
          return []
        }
      }),
      tap((events: EventWithStatus[]) => {
        this.subscriber.next({
          type: `GET_${this.id}S`,
          data: events,
        })
      }),
      catchError((error: any): Observable<EventWithStatus[]> => {
        console.error('Error fetching events:', error)
        return of([])
      }),
    )
  }

  /**
   * Get event object type "CreateEventInput" by convert model to use when duplicate
   * @param model The events with status object
   * @returns
   */
  getEventInputToDuplicate(model: any) {
    const event: Partial<CreateEventInput> = {}

    const categories: string[] = []

    if (model.category) {
      for (let index = 0; index < model.category.length; index++) {
        if (model.categories[index].id == model.category[index]) {
          categories.push(model.categories[index].name)
        }
      }
    }

    event.client_id = model.client_id
    event.name = model.name
    event.external_code = ''
    event.category = categories
    event.password = model.password
    event.opening_time = model.opening_time
    event.starting_time = model.starting_time
    event.predefined_location_id = model.predefined_location_id
    event.set_predefined_location = false
    event.closing_time = model.closing_time
    event.max_participants = model.max_participants
    event.max_enforce = model.max_enforce
    event.restrictions = model.restrictions
    event.club_ids = model.club_ids
    event.keep_open = model.keep_open
    event.max_participants = model.max_participants || 0
    event.lat = model.location.lat
    event.lon = model.location.lon
    event.allow_transfer = model.allow_transfer
    event.station_type = model.station_type
    event.location_name = model.location
    event.location_display_name = model.location.display_name
    event.location_description = model.location.description
    event.location_address = model.location.address
    event.active = model.active
    event.image = model.image
    event.entrances = model.entrancesAmount - 1
    event.tickets_url = model.tickets_url || ''

    return event
  }

  getSortedEntrancesAndStations(model: EventWithStatus) {
    const filteredData = model?.entrances_statistics?.filter((en: any) => en.name != 'event_unused_entrance')
    const filteredEntrances = filteredData?.sort((a: any, b: any) => parseInt(a.name) - parseInt(b.name) || a.name - b.name || a.name.localeCompare(b.name))

    filteredEntrances?.forEach((element: any) => {
      element.stations_statistics.sort((a: any, b: any) => parseInt(a.name) - parseInt(b.name) || a?.name - b.name || a?.name.localeCompare(b.name))
    })

    return filteredEntrances
  }

  setEventTimeByClientGMT(model: any) {
    model.opening_time = this.dateService.convertToISOWithTimezone(model.opening_time)
    model.starting_time = this.dateService.convertToISOWithTimezone(model.starting_time)
    model.closing_time = this.dateService.convertToISOWithTimezone(model.closing_time)
  }
}
